import { useEffect, useMemo, useRef, useState } from 'react'
import {
    Search,
    X,
    Users,
    Building2,
    TrendingUp,
    UserCheck,
    Check,
    Loader2,
    AtSign,
    ChevronDown,
} from 'lucide-react'
import { adminApi } from '@/services/adminApi'
import type { User, Organization } from '@/types/admin'
import type { BroadcastAudience } from '@/types/communications'
import { Card, CardContent, CardHeader, CardTitle } from './_shared'

/*
 * AudiencePicker
 *
 * A single composed control that replaces the old 4-radio-card + textarea UI.
 * Aesthetic: Linear-style segmented control + typeahead-first picking. Tight,
 * precise, workflow-focused — no fluff. Recipients become visual chips with
 * avatar dots; org IDs chipify; the footer shows a live blast-radius count.
 */

// ─── Avatar dot helper (hash email → color) ─────────────────────────────────
const AVATAR_PALETTE = [
    { bg: 'bg-rose-100', fg: 'text-rose-700', ring: 'ring-rose-200' },
    { bg: 'bg-amber-100', fg: 'text-amber-700', ring: 'ring-amber-200' },
    { bg: 'bg-emerald-100', fg: 'text-emerald-700', ring: 'ring-emerald-200' },
    { bg: 'bg-cyan-100', fg: 'text-cyan-700', ring: 'ring-cyan-200' },
    { bg: 'bg-violet-100', fg: 'text-violet-700', ring: 'ring-violet-200' },
    { bg: 'bg-fuchsia-100', fg: 'text-fuchsia-700', ring: 'ring-fuchsia-200' },
    { bg: 'bg-sky-100', fg: 'text-sky-700', ring: 'ring-sky-200' },
    { bg: 'bg-orange-100', fg: 'text-orange-700', ring: 'ring-orange-200' },
]

function avatarFor(seed: string) {
    let hash = 0
    for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0
    return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]
}

function initialOf(u: { name?: string | null; email: string }): string {
    const src = (u.name && u.name.trim()) || u.email
    return src.charAt(0).toUpperCase()
}

function AvatarDot({ user, size = 'md' }: { user: { name?: string | null; email: string }; size?: 'sm' | 'md' }) {
    const a = avatarFor(user.email.toLowerCase())
    const dim = size === 'sm' ? 'h-5 w-5 text-[10px]' : 'h-6 w-6 text-[11px]'
    return (
        <span
            className={`inline-flex ${dim} shrink-0 items-center justify-center rounded-full font-semibold ring-1 ${a.bg} ${a.fg} ${a.ring}`}
        >
            {initialOf(user)}
        </span>
    )
}

// ─── Segmented control (the 4-mode switcher) ────────────────────────────────
type AudienceMode = BroadcastAudience

const MODES: {
    value: AudienceMode
    label: string
    icon: React.ComponentType<{ className?: string }>
}[] = [
    { value: 'ALL_USERS', label: 'Everyone', icon: Users },
    { value: 'BY_ORG', label: 'By org', icon: Building2 },
    { value: 'BY_FEATURE_USAGE', label: 'By feature', icon: TrendingUp },
    { value: 'BY_USERS', label: 'Specific users', icon: UserCheck },
]

function SegmentedControl({
    value,
    onChange,
}: {
    value: AudienceMode
    onChange: (v: AudienceMode) => void
}) {
    return (
        <div className="inline-flex w-full rounded-lg bg-gray-100 p-1 ring-1 ring-gray-200/70">
            {MODES.map(mode => {
                const Icon = mode.icon
                const active = value === mode.value
                return (
                    <button
                        key={mode.value}
                        type="button"
                        onClick={() => onChange(mode.value)}
                        className={[
                            'group relative flex flex-1 items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-all duration-150',
                            active
                                ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200/80'
                                : 'text-gray-600 hover:text-gray-900',
                        ].join(' ')}
                    >
                        <Icon
                            className={`h-3.5 w-3.5 transition-colors ${
                                active ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                            }`}
                        />
                        <span className="truncate">{mode.label}</span>
                    </button>
                )
            })}
        </div>
    )
}

// ─── Token parsing ──────────────────────────────────────────────────────────
function tokenize(raw: string): string[] {
    return raw
        .split(/[\s,]+/)
        .map(s => s.trim())
        .filter(Boolean)
}

// ─── Main component ─────────────────────────────────────────────────────────
export type FeatureOperator = 'gt' | 'gte' | 'lt' | 'lte' | 'eq'

export interface AudiencePickerProps {
    audience: AudienceMode
    onAudienceChange: (v: AudienceMode) => void
    orgIds: string
    onOrgIdsChange: (v: string) => void
    feature: string
    onFeatureChange: (v: string) => void
    featureOperator?: FeatureOperator
    onFeatureOperatorChange?: (v: FeatureOperator) => void
    featureThreshold?: number | null
    onFeatureThresholdChange?: (v: number | null) => void
    userList: string
    onUserListChange: (v: string) => void
}

const FEATURE_OPTIONS = [
    { key: 'chat', label: 'Chat' },
    { key: 'meetings', label: 'Meetings' },
    { key: 'workflows', label: 'Workflows' },
    { key: 'arc', label: 'ARC' },
    { key: 'libra_apps', label: 'Libra Apps' },
    { key: 'email_assistant', label: 'Email Assistant' },
]

const OPERATOR_OPTIONS: { value: FeatureOperator; symbol: string; label: string }[] = [
    { value: 'gte', symbol: '≥', label: 'at least' },
    { value: 'gt', symbol: '>', label: 'more than' },
    { value: 'lte', symbol: '≤', label: 'at most' },
    { value: 'lt', symbol: '<', label: 'less than' },
    { value: 'eq', symbol: '=', label: 'exactly' },
]

export function AudiencePicker({
    audience,
    onAudienceChange,
    orgIds,
    onOrgIdsChange,
    feature,
    onFeatureChange,
    featureOperator = 'gte',
    onFeatureOperatorChange,
    featureThreshold = null,
    onFeatureThresholdChange,
    userList,
    onUserListChange,
}: AudiencePickerProps) {
    // ── Shared cache of resolved user objects so chips can render rich info
    const [userCache, setUserCache] = useState<Record<string, User>>({})

    // ── Search typeahead state (only relevant in BY_USERS)
    const [search, setSearch] = useState('')
    const [searchResults, setSearchResults] = useState<User[]>([])
    const [searchOpen, setSearchOpen] = useState(false)
    const [searching, setSearching] = useState(false)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const boxRef = useRef<HTMLDivElement | null>(null)

    // ── Orgs (for BY_ORG picker)
    const [orgs, setOrgs] = useState<Organization[]>([])
    const [orgsLoaded, setOrgsLoaded] = useState(false)
    const [orgMenuOpen, setOrgMenuOpen] = useState(false)
    const orgBoxRef = useRef<HTMLDivElement | null>(null)

    // Parsed tokens
    const userTokens = useMemo(() => tokenize(userList), [userList])
    const orgTokens = useMemo(() => tokenize(orgIds), [orgIds])

    // Server returns { users: [...], pagination }, but the type lies about
    // `.data`. Extract defensively so this works even if the type is fixed later.
    function extractUsers(res: unknown): User[] {
        if (!res || typeof res !== 'object') return []
        const r = res as { users?: User[]; data?: User[] }
        if (Array.isArray(r.users)) return r.users
        if (Array.isArray(r.data)) return r.data
        return []
    }

    // ── Hydrate user cache for any tokens that look like emails OR IDs we don't have yet
    useEffect(() => {
        const missing = userTokens.filter(t => !userCache[t.toLowerCase()] && !userCache[t])
        if (missing.length === 0) return
        let cancelled = false
        ;(async () => {
            const next: Record<string, User> = {}
            for (const t of missing) {
                try {
                    const r = await adminApi.getUsers({ search: t, limit: 1 })
                    if (cancelled) return
                    const list = extractUsers(r)
                    const hit = list.find(
                        u =>
                            u.id === t ||
                            u.email.toLowerCase() === t.toLowerCase(),
                    )
                    if (hit) next[hit.email.toLowerCase()] = hit
                } catch {
                    /* ignore — chip stays in "raw" mode */
                }
            }
            if (!cancelled && Object.keys(next).length) {
                setUserCache(prev => ({ ...prev, ...next }))
            }
        })()
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userTokens.join('|')])

    // ── Search debounce
    useEffect(() => {
        if (audience !== 'BY_USERS') return
        if (!search.trim()) {
            setSearchResults([])
            return
        }
        setSearching(true)
        const t = setTimeout(async () => {
            try {
                const r = await adminApi.getUsers({ search: search.trim(), limit: 8 })
                const list = extractUsers(r)
                setSearchResults(list)
                // Opportunistically cache
                setUserCache(prev => {
                    const next = { ...prev }
                    for (const u of list) next[u.email.toLowerCase()] = u
                    return next
                })
            } finally {
                setSearching(false)
            }
        }, 180)
        return () => clearTimeout(t)
    }, [search, audience])

    // ── Outside click close
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
                setSearchOpen(false)
            }
            if (orgBoxRef.current && !orgBoxRef.current.contains(e.target as Node)) {
                setOrgMenuOpen(false)
            }
        }
        document.addEventListener('mousedown', onClick)
        return () => document.removeEventListener('mousedown', onClick)
    }, [])

    // ── Lazy-load orgs when BY_ORG becomes active
    useEffect(() => {
        if (audience !== 'BY_ORG' || orgsLoaded) return
        ;(async () => {
            try {
                const list = await adminApi.getOrganizations()
                setOrgs(list)
            } finally {
                setOrgsLoaded(true)
            }
        })()
    }, [audience, orgsLoaded])

    // ── Adding / removing recipients
    function addUser(u: User) {
        const current = new Set(userTokens.map(t => t.toLowerCase()))
        if (current.has(u.email.toLowerCase()) || current.has(u.id.toLowerCase())) return
        onUserListChange([...userTokens, u.email].join('\n'))
        setUserCache(prev => ({ ...prev, [u.email.toLowerCase()]: u }))
        setSearch('')
        setSearchResults([])
        setSearchOpen(false)
        inputRef.current?.focus()
    }

    function removeUserToken(token: string) {
        onUserListChange(
            userTokens.filter(t => t.toLowerCase() !== token.toLowerCase()).join('\n'),
        )
    }

    function addUserByRaw(raw: string) {
        const cleaned = raw.trim()
        if (!cleaned) return
        const current = new Set(userTokens.map(t => t.toLowerCase()))
        if (current.has(cleaned.toLowerCase())) return
        onUserListChange([...userTokens, cleaned].join('\n'))
        setSearch('')
    }

    function toggleOrg(id: string) {
        const set = new Set(orgTokens)
        if (set.has(id)) set.delete(id)
        else set.add(id)
        onOrgIdsChange(Array.from(set).join(', '))
    }

    function removeOrgToken(id: string) {
        onOrgIdsChange(orgTokens.filter(t => t !== id).join(', '))
    }

    // ── Key handler for search box: Enter commits raw token; Backspace on empty removes last chip
    function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter' && search.trim()) {
            e.preventDefault()
            // If exact result in dropdown, pick it; else treat as raw token
            const exact =
                searchResults.find(
                    u =>
                        u.email.toLowerCase() === search.trim().toLowerCase() ||
                        u.id === search.trim(),
                ) ?? searchResults[0]
            if (exact) addUser(exact)
            else addUserByRaw(search)
        } else if (e.key === 'Backspace' && !search && userTokens.length > 0) {
            removeUserToken(userTokens[userTokens.length - 1])
        } else if (e.key === 'Escape') {
            setSearchOpen(false)
        }
    }

    // ── Recipient chip (resolved-or-raw) for BY_USERS
    function UserChip({ token }: { token: string }) {
        const key = token.toLowerCase()
        const resolved =
            userCache[key] ??
            Object.values(userCache).find(u => u.id === token)
        if (resolved) {
            return (
                <span className="group inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-white py-0.5 pl-1 pr-2 text-xs shadow-sm transition-shadow hover:shadow-md">
                    <AvatarDot user={resolved} size="sm" />
                    <span className="truncate font-medium text-gray-900">
                        {resolved.name ?? resolved.email.split('@')[0]}
                    </span>
                    <span className="hidden truncate text-gray-400 sm:inline">
                        · {resolved.email}
                    </span>
                    <button
                        type="button"
                        onClick={() => removeUserToken(token)}
                        className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-900/5 hover:text-gray-900"
                        aria-label={`Remove ${resolved.email}`}
                    >
                        <X className="h-3 w-3" />
                    </button>
                </span>
            )
        }
        // Raw (unresolved) — show as muted mono pill
        const isEmail = token.includes('@')
        return (
            <span className="group inline-flex items-center gap-1.5 rounded-full border border-dashed border-gray-300 bg-gray-50 px-2 py-0.5 text-xs transition-colors hover:border-gray-400">
                {isEmail ? (
                    <AtSign className="h-3 w-3 text-gray-400" />
                ) : (
                    <span className="font-mono text-[10px] text-gray-400">id</span>
                )}
                <span className="truncate font-mono text-gray-700">{token}</span>
                <button
                    type="button"
                    onClick={() => removeUserToken(token)}
                    className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-900/5 hover:text-gray-900"
                    aria-label={`Remove ${token}`}
                >
                    <X className="h-3 w-3" />
                </button>
            </span>
        )
    }

    // ── Live count summary
    const countSummary = useMemo(() => {
        if (audience === 'ALL_USERS') {
            return { label: 'Every active user with a verified email', tone: 'ok' as const }
        }
        if (audience === 'BY_ORG') {
            const n = orgTokens.length
            if (n === 0) return { label: 'No organizations selected', tone: 'warn' as const }
            const totalUsers = orgTokens
                .map(id => orgs.find(o => o.id === id)?._count?.users ?? 0)
                .reduce((a, b) => a + b, 0)
            return {
                label: `${n} org${n === 1 ? '' : 's'}${
                    orgsLoaded && totalUsers ? ` · ~${totalUsers.toLocaleString()} users` : ''
                }`,
                tone: 'ok' as const,
            }
        }
        if (audience === 'BY_FEATURE_USAGE') {
            if (!feature.trim()) return { label: 'Pick a feature', tone: 'warn' as const }
            if (typeof featureThreshold !== 'number') {
                return { label: `Users whose last activity was ${feature.trim()}`, tone: 'ok' as const }
            }
            const opLabel = OPERATOR_OPTIONS.find(o => o.value === featureOperator)?.label ?? '≥'
            return {
                label: `Users with ${opLabel} ${featureThreshold} ${feature.trim()} events`,
                tone: 'ok' as const,
            }
        }
        const n = userTokens.length
        return {
            label: n === 0 ? 'No recipients yet' : `${n} recipient${n === 1 ? '' : 's'}`,
            tone: n === 0 ? ('warn' as const) : ('ok' as const),
        }
    }, [audience, orgTokens, orgs, orgsLoaded, feature, userTokens])

    return (
        <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle className="!text-sm">Audience</CardTitle>
                <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        countSummary.tone === 'ok'
                            ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                            : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                    }`}
                >
                    {countSummary.tone === 'ok' ? (
                        <Check className="h-3 w-3" />
                    ) : (
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                    )}
                    {countSummary.label}
                </span>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Segmented mode switch */}
                <SegmentedControl value={audience} onChange={onAudienceChange} />

                {/* Mode-specific body */}
                <div className="animate-[fade-in_150ms_ease-out]">
                    {audience === 'ALL_USERS' && (
                        <div className="rounded-lg border border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-white px-4 py-5">
                            <div className="flex items-start gap-3">
                                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 ring-1 ring-emerald-200">
                                    <Users className="h-4 w-4 text-emerald-700" />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-emerald-900">
                                        Broadcasting to every active user
                                    </div>
                                    <p className="mt-0.5 text-xs text-emerald-800/80">
                                        Filters: <code className="rounded bg-emerald-100/70 px-1 font-mono text-[11px] text-emerald-900">status=active</code>{' '}
                                        + <code className="rounded bg-emerald-100/70 px-1 font-mono text-[11px] text-emerald-900">email ≠ ''</code>.
                                        Suppressions still apply.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {audience === 'BY_ORG' && (
                        <div className="space-y-3">
                            {/* Selected-org chips */}
                            <div className="flex flex-wrap gap-1.5 rounded-lg border border-gray-200 bg-white p-2 min-h-[42px] items-center">
                                {orgTokens.length === 0 && (
                                    <span className="px-1 text-xs italic text-gray-400">
                                        No orgs selected yet
                                    </span>
                                )}
                                {orgTokens.map(id => {
                                    const org = orgs.find(o => o.id === id)
                                    return (
                                        <span
                                            key={id}
                                            className="group inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 py-0.5 pl-2 pr-1.5 text-xs text-blue-900 transition-shadow hover:shadow-sm"
                                        >
                                            <Building2 className="h-3 w-3 text-blue-600" />
                                            <span className="truncate font-medium">
                                                {org?.name ?? id}
                                            </span>
                                            {org?._count?.users !== undefined && (
                                                <span className="rounded bg-blue-100 px-1 font-mono text-[10px] text-blue-700">
                                                    {org._count.users}
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => removeOrgToken(id)}
                                                className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-blue-500 transition-colors hover:bg-blue-200/60 hover:text-blue-900"
                                                aria-label={`Remove ${org?.name ?? id}`}
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    )
                                })}
                            </div>

                            {/* Picker */}
                            <div ref={orgBoxRef} className="relative">
                                <button
                                    type="button"
                                    onClick={() => setOrgMenuOpen(v => !v)}
                                    className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-900 shadow-sm hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <span className="flex items-center gap-2 text-gray-600">
                                        <Building2 className="h-4 w-4 text-gray-400" />
                                        {orgsLoaded
                                            ? `Choose from ${orgs.length} organization${orgs.length === 1 ? '' : 's'}`
                                            : 'Loading organizations…'}
                                    </span>
                                    <ChevronDown className="h-4 w-4 text-gray-400" />
                                </button>
                                {orgMenuOpen && orgsLoaded && (
                                    <div className="absolute z-10 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg ring-1 ring-black/5">
                                        {orgs.length === 0 && (
                                            <div className="p-3 text-xs italic text-gray-500">
                                                No organizations found.
                                            </div>
                                        )}
                                        {orgs.map(o => {
                                            const selected = orgTokens.includes(o.id)
                                            return (
                                                <button
                                                    key={o.id}
                                                    type="button"
                                                    onClick={() => toggleOrg(o.id)}
                                                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-xs transition-colors ${
                                                        selected
                                                            ? 'bg-blue-50 text-blue-900'
                                                            : 'hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <span className="flex min-w-0 items-center gap-2">
                                                        <span
                                                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                                                selected
                                                                    ? 'border-blue-600 bg-blue-600 text-white'
                                                                    : 'border-gray-300 bg-white'
                                                            }`}
                                                        >
                                                            {selected && <Check className="h-3 w-3" />}
                                                        </span>
                                                        <span className="truncate">
                                                            <span className="font-medium text-gray-900">
                                                                {o.name}
                                                            </span>
                                                            <span className="ml-1 text-gray-500">
                                                                · {o.emailDomain}
                                                            </span>
                                                        </span>
                                                    </span>
                                                    <span className="shrink-0 rounded bg-gray-100 px-1.5 font-mono text-[10px] text-gray-600">
                                                        {o._count?.users ?? 0}
                                                    </span>
                                                </button>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Escape hatch: paste IDs manually */}
                            <details className="text-xs text-gray-500">
                                <summary className="cursor-pointer select-none hover:text-gray-900">
                                    Paste IDs manually instead
                                </summary>
                                <textarea
                                    value={orgIds}
                                    onChange={e => onOrgIdsChange(e.target.value)}
                                    rows={2}
                                    placeholder="cmo2…, cmo3…"
                                    className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                            </details>
                        </div>
                    )}

                    {audience === 'BY_FEATURE_USAGE' && (
                        <div className="space-y-3">
                            {/* Feature select (with custom fallback) */}
                            <div>
                                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                    Feature
                                </label>
                                <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
                                    {FEATURE_OPTIONS.map(opt => {
                                        const active = feature === opt.key
                                        return (
                                            <button
                                                key={opt.key}
                                                type="button"
                                                onClick={() => onFeatureChange(opt.key)}
                                                className={`rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors ${
                                                    active
                                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                {opt.label}
                                            </button>
                                        )
                                    })}
                                </div>
                                <input
                                    value={
                                        FEATURE_OPTIONS.some(o => o.key === feature)
                                            ? ''
                                            : feature
                                    }
                                    onChange={e => onFeatureChange(e.target.value)}
                                    placeholder="…or type a custom feature key"
                                    className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-1.5 font-mono text-[11px] text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                            </div>

                            {/* Operator + threshold */}
                            <div>
                                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                    Usage filter{' '}
                                    <span className="font-normal normal-case tracking-normal text-gray-400">
                                        · optional — leave threshold blank to target last-active-feature matches instead
                                    </span>
                                </label>
                                <div className="flex gap-1.5">
                                    <div className="inline-flex shrink-0 rounded-md bg-gray-100 p-1 ring-1 ring-gray-200/70">
                                        {OPERATOR_OPTIONS.map(op => {
                                            const active = featureOperator === op.value
                                            return (
                                                <button
                                                    key={op.value}
                                                    type="button"
                                                    onClick={() =>
                                                        onFeatureOperatorChange?.(op.value)
                                                    }
                                                    title={op.label}
                                                    className={`h-8 w-9 rounded text-[13px] font-semibold transition-all ${
                                                        active
                                                            ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200/80'
                                                            : 'text-gray-500 hover:text-gray-900'
                                                    }`}
                                                >
                                                    {op.symbol}
                                                </button>
                                            )
                                        })}
                                    </div>
                                    <input
                                        type="number"
                                        min={0}
                                        value={
                                            typeof featureThreshold === 'number'
                                                ? featureThreshold
                                                : ''
                                        }
                                        onChange={e => {
                                            const v = e.target.value
                                            if (v === '') {
                                                onFeatureThresholdChange?.(null)
                                            } else {
                                                const n = Number(v)
                                                onFeatureThresholdChange?.(
                                                    Number.isFinite(n) ? n : null,
                                                )
                                            }
                                        }}
                                        placeholder="threshold, e.g. 100"
                                        className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 shadow-sm tabular-nums focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    />
                                </div>
                                <p className="mt-2 text-[11px] leading-5 text-gray-500">
                                    Counts from{' '}
                                    <code className="rounded bg-gray-100 px-1 font-mono text-[10px]">
                                        user_activity_summary.featureUsageJson
                                    </code>
                                    . E.g.{' '}
                                    <code className="rounded bg-gray-100 px-1 font-mono text-[10px]">
                                        chat ≥ 100
                                    </code>{' '}
                                    targets power users;{' '}
                                    <code className="rounded bg-gray-100 px-1 font-mono text-[10px]">
                                        meetings &lt; 5
                                    </code>{' '}
                                    targets light users.
                                </p>
                            </div>
                        </div>
                    )}

                    {audience === 'BY_USERS' && (
                        <div className="space-y-3">
                            {/* Combined recipient box: chips + typeahead input */}
                            <div
                                ref={boxRef}
                                className={`relative rounded-lg border bg-white transition-shadow ${
                                    searchOpen
                                        ? 'border-blue-500 ring-2 ring-blue-500/15'
                                        : 'border-gray-300 shadow-sm'
                                }`}
                            >
                                <div className="flex flex-wrap items-center gap-1.5 p-1.5">
                                    {userTokens.map(t => (
                                        <UserChip key={t} token={t} />
                                    ))}
                                    <div className="relative flex min-w-[160px] flex-1 items-center">
                                        <Search className="absolute left-2 h-3.5 w-3.5 text-gray-400" />
                                        <input
                                            ref={inputRef}
                                            value={search}
                                            onChange={e => {
                                                setSearch(e.target.value)
                                                setSearchOpen(true)
                                            }}
                                            onFocus={() => setSearchOpen(true)}
                                            onKeyDown={onSearchKeyDown}
                                            placeholder={
                                                userTokens.length === 0
                                                    ? 'Search by name or email — or paste a user ID'
                                                    : 'Add another…'
                                            }
                                            className="w-full border-0 bg-transparent py-1.5 pl-7 pr-2 text-xs text-gray-900 placeholder-gray-400 focus:outline-none"
                                        />
                                    </div>
                                </div>

                                {/* Dropdown */}
                                {searchOpen && search.trim() && (
                                    <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg ring-1 ring-black/5">
                                        {searching && (
                                            <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500">
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                Searching…
                                            </div>
                                        )}
                                        {!searching && searchResults.length === 0 && (
                                            <div className="px-3 py-2 text-xs text-gray-500">
                                                No user matches{' '}
                                                <code className="rounded bg-gray-100 px-1 font-mono text-[11px]">
                                                    {search}
                                                </code>
                                                .{' '}
                                                <button
                                                    type="button"
                                                    onClick={() => addUserByRaw(search)}
                                                    className="font-medium text-blue-600 hover:underline"
                                                >
                                                    Add as raw{' '}
                                                    {search.includes('@') ? 'email' : 'ID'}
                                                </button>
                                            </div>
                                        )}
                                        {!searching &&
                                            searchResults.map(u => {
                                                const already = userTokens.some(
                                                    t =>
                                                        t.toLowerCase() ===
                                                            u.email.toLowerCase() ||
                                                        t === u.id,
                                                )
                                                return (
                                                    <button
                                                        key={u.id}
                                                        type="button"
                                                        disabled={already}
                                                        onClick={() => addUser(u)}
                                                        className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs transition-colors ${
                                                            already
                                                                ? 'cursor-not-allowed opacity-50'
                                                                : 'hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        <AvatarDot user={u} size="sm" />
                                                        <span className="flex min-w-0 flex-1 flex-col">
                                                            <span className="truncate font-medium text-gray-900">
                                                                {u.name ?? u.email.split('@')[0]}
                                                            </span>
                                                            <span className="truncate text-[11px] text-gray-500">
                                                                {u.email}
                                                                {u.organization && (
                                                                    <span className="text-gray-400">
                                                                        {' '}
                                                                        · {u.organization.name}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </span>
                                                        {already ? (
                                                            <Check className="h-3.5 w-3.5 text-emerald-600" />
                                                        ) : (
                                                            <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-gray-500 group-hover:bg-blue-100 group-hover:text-blue-700">
                                                                add
                                                            </span>
                                                        )}
                                                    </button>
                                                )
                                            })}
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-between text-[11px] text-gray-500">
                                <span>
                                    Type to search.{' '}
                                    <kbd className="rounded border border-gray-200 bg-gray-50 px-1 font-mono text-[10px]">
                                        Enter
                                    </kbd>{' '}
                                    adds the raw value.{' '}
                                    <kbd className="rounded border border-gray-200 bg-gray-50 px-1 font-mono text-[10px]">
                                        Backspace
                                    </kbd>{' '}
                                    removes last chip.
                                </span>
                                {userTokens.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => onUserListChange('')}
                                        className="font-medium text-gray-500 transition-colors hover:text-red-600"
                                    >
                                        Clear all
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}
